import { isNoChargeOrder } from "./no-charge";

export const BUYER_PAYMENT_STATES = [
  "no_charge",
  "unbilled",
  "billed",
  "partially_paid",
  "paid",
  "partially_refunded",
  "refunded",
] as const;

export type BuyerPaymentState = (typeof BUYER_PAYMENT_STATES)[number];

export interface BuyerPaymentInput {
  paymentStatus?: string;
  payment?: {
    paidAt?: number;
    invoiceStatus?: string;
    stripeInvoiceId?: string;
    isFullRefund?: boolean;
    refundAmount?: number;
    collection?: string;
  };
  pricing?: { buyerGrandTotalCents?: number };
}

const BILLED_INVOICE_STATUSES: ReadonlySet<string> = new Set(["open", "overdue"]);

function isFullyRefunded(order: BuyerPaymentInput): boolean {
  if (order.payment?.isFullRefund !== undefined) {
    return order.payment.isFullRefund;
  }
  const refundAmount = order.payment?.refundAmount;
  const buyerGrandTotal = order.pricing?.buyerGrandTotalCents;
  return (
    refundAmount !== undefined &&
    buyerGrandTotal !== undefined &&
    Number.isFinite(refundAmount) &&
    Number.isFinite(buyerGrandTotal) &&
    buyerGrandTotal > 0 &&
    refundAmount >= buyerGrandTotal
  );
}

function isBilled(order: BuyerPaymentInput): boolean {
  if (order.paymentStatus === "invoiced") return true;
  const payment = order.payment;
  return (
    payment?.stripeInvoiceId !== undefined &&
    payment.invoiceStatus !== undefined &&
    BILLED_INVOICE_STATUSES.has(payment.invoiceStatus)
  );
}

function wasCaptured(order: BuyerPaymentInput): boolean {
  const paidAt = order.payment?.paidAt;
  return paidAt !== undefined && paidAt > 0;
}

export function resolveBuyerPayment(order: BuyerPaymentInput): BuyerPaymentState {
  if (order.paymentStatus === "refunded") {
    return isFullyRefunded(order) ? "refunded" : "partially_refunded";
  }
  if (order.paymentStatus === "paid" || wasCaptured(order)) return "paid";
  if (isNoChargeOrder(order)) return "no_charge";
  if (order.payment?.invoiceStatus === "partially_paid") return "partially_paid";
  return isBilled(order) ? "billed" : "unbilled";
}

export function buyerWasCharged(order: BuyerPaymentInput): boolean {
  const state = resolveBuyerPayment(order);
  return state === "paid" || state === "partially_refunded" || state === "refunded";
}

export function buyerHoldsPayment(order: BuyerPaymentInput): boolean {
  const state = resolveBuyerPayment(order);
  return state === "paid" || state === "partially_refunded";
}

export function isBuyerFullyRefunded(order: BuyerPaymentInput): boolean {
  return resolveBuyerPayment(order) === "refunded";
}

export function paidAtCheckout(order: { payment?: { checkoutAttemptId?: string; stripeInvoiceId?: string } }): boolean {
  return order.payment?.checkoutAttemptId !== undefined && order.payment.stripeInvoiceId === undefined;
}
